<template>
  <div class="image-viewer">
    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <span class="loading-text">加载中...</span>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <span class="error-text">{{ error }}</span>
      <button @click="loadImage" class="retry-btn">重试</button>
    </div>

    <!-- 图片展示 -->
    <div v-else-if="imageUrl" class="image-container">
      <img 
        :src="imageUrl" 
        :alt="fileName || '图片'"
        class="image"
        :class="{ 'display-only': displayOnly }"
        @load="handleImageLoad"
        @error="handleImageError"
        @click="displayOnly ? undefined : handleImageClick"
      />
      
      <!-- 图片信息 -->
      <div v-if="showInfo && !displayOnly" class="image-info">
        <div class="info-item">
          <span class="label">文件名:</span>
          <span class="value">{{ fileName }}</span>
        </div>
        <div class="info-item">
          <span class="label">文件大小:</span>
          <span class="value">{{ formatFileSize(fileSize) }}</span>
        </div>
        <div class="info-item">
          <span class="label">存储类型:</span>
          <span class="value">{{ storageType === 'cloud' ? '云端' : '本地' }}</span>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div v-if="!displayOnly" class="action-buttons">
        <button @click="downloadImage" class="action-btn download" title="下载图片">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7,10 12,15 17,10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <button @click="copyImageUrl" class="action-btn copy" title="复制图片链接">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        </button>
        <button @click="toggleFullscreen" class="action-btn fullscreen" title="全屏查看">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15,3 21,3 21,9"/>
            <polyline points="9,21 3,21 3,15"/>
            <line x1="21" y1="3" x2="14" y2="10"/>
            <line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-container">
      <div class="empty-icon">🖼️</div>
      <span class="empty-text">暂无图片</span>
    </div>

    <!-- 全屏模态框 -->
    <div v-if="isFullscreen && !displayOnly" class="fullscreen-modal" @click="closeFullscreen">
      <div class="fullscreen-content" @click.stop>
        <img 
          :src="imageUrl" 
          :alt="fileName || '图片'"
          class="fullscreen-image"
        />
        <button @click="closeFullscreen" class="close-btn">×</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { FileStorageApi } from '@/api/fileStorage'
import type { FileUrlResponse } from '@/api/fileStorage'

interface Props {
  fileCode: string
  fileName?: string
  fileSize?: number
  showInfo?: boolean
  width?: string
  height?: string
  fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
  displayOnly?: boolean // 新增：只展示模式，不显示任何交互元素
}

const props = withDefaults(defineProps<Props>(), {
  showInfo: true,
  width: '100%',
  height: 'auto',
  fit: 'contain',
  displayOnly: false // 默认不是只展示模式
})

const emit = defineEmits<{
  'image-loaded': [fileInfo: FileUrlResponse]
  'image-error': [error: string]
  'download-clicked': [fileCode: string]
}>()

// 响应式状态
const isLoading = ref(false)
const error = ref<string | null>(null)
const imageUrl = ref<string | undefined>(undefined)
const fileName = ref<string>('')
const fileSize = ref<number>(0)
const storageType = ref<string>('')
const isFullscreen = ref(false)

// 加载图片
const loadImage = async () => {
  if (!props.fileCode) {
    error.value = '文件编码不能为空'
    return
  }

  isLoading.value = true
  error.value = null

  try {
    const response = await FileStorageApi.getFileUrl(props.fileCode)
    
    if (response.success && response.data) {
      const fileInfo = response.data
      
      // 处理URL：如果URL不包含http，则使用VITE_API_BASE_URL拼接
      let processedUrl = fileInfo.fileUrl
      if (!processedUrl.startsWith('http')) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
        // 避免双斜杠问题：确保apiBaseUrl不以/结尾，fileUrl不以/开头
        const cleanApiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl
        const cleanFileUrl = processedUrl.startsWith('/') ? processedUrl : `/${processedUrl}`
        processedUrl = `${cleanApiBaseUrl}${cleanFileUrl}`
      }
      
      imageUrl.value = processedUrl
      fileName.value = fileInfo.fileName
      fileSize.value = fileInfo.fileSize
      storageType.value = fileInfo.storageType
      
      emit('image-loaded', fileInfo)
    } else {
      error.value = response.message || '获取图片URL失败'
      emit('image-error', error.value)
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载图片失败'
    emit('image-error', error.value)
  } finally {
    isLoading.value = false
  }
}

// 图片加载成功
const handleImageLoad = () => {
}

// 图片加载失败
const handleImageError = () => {
  error.value = '图片加载失败'
  emit('image-error', error.value)
}

// 点击图片
const handleImageClick = () => {
  if (props.displayOnly) return // 只展示模式下不响应点击
  if (props.showInfo) {
    toggleFullscreen()
  }
}

// 下载图片
const downloadImage = async () => {
  if (!props.fileCode) return
  
  try {
    emit('download-clicked', props.fileCode)
    
    // 创建下载链接
    const link = document.createElement('a')
    link.href = imageUrl.value!
    link.download = fileName.value || 'image'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (err) {
    console.error('下载失败:', err)
    alert('下载失败')
  }
}

// 复制图片URL
const copyImageUrl = async () => {
  if (!imageUrl.value) return
  
  try {
    await navigator.clipboard.writeText(imageUrl.value)
    alert('图片链接已复制到剪贴板')
  } catch (err) {
    console.error('复制失败:', err)
    alert('复制失败')
  }
}

// 切换全屏
const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
}

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

// 格式化文件大小
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 监听fileCode变化
watch(() => props.fileCode, (newFileCode) => {
  if (newFileCode) {
    loadImage()
  } else {
    imageUrl.value = undefined
    error.value = null
  }
}, { immediate: true })

// 组件挂载时加载图片
onMounted(() => {
  if (props.fileCode) {
    loadImage()
  }
})

// 暴露方法
defineExpose({
  loadImage,
  downloadImage,
  copyImageUrl,
  toggleFullscreen
})
</script>

<style scoped>
.image-viewer {
  position: relative;
  width: v-bind(width);
  height: v-bind(height);
  border-radius: 8px;
  overflow: hidden;
}

/* 加载状态 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  color: #6b7280;
}

/* 错误状态 */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
  padding: 20px;
}

.error-icon {
  font-size: 32px;
}

.error-text {
  font-size: 14px;
  color: #ef4444;
  text-align: center;
}

.retry-btn {
  padding: 8px 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background-color: #2563eb;
}

/* 图片容器 */
.image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: v-bind(fit);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image:hover {
  transform: scale(1.02);
}

.image.display-only {
  cursor: default;
}

.image.display-only:hover {
  transform: none;
}

/* 图片信息 */
.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 16px;
  font-size: 12px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 500;
}

.value {
  opacity: 0.9;
}

/* 操作按钮 */
.action-buttons {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .action-buttons {
  opacity: 1;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.action-btn.download {
  background-color: #10b981;
}

.action-btn.download:hover {
  background-color: #059669;
}

.action-btn.copy {
  background-color: #3b82f6;
}

.action-btn.copy:hover {
  background-color: #2563eb;
}

.action-btn.fullscreen {
  background-color: #8b5cf6;
}

.action-btn.fullscreen:hover {
  background-color: #7c3aed;
}

/* 空状态 */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 12px;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-text {
  font-size: 14px;
  color: #6b7280;
}

/* 全屏模态框 */
.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.fullscreen-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.fullscreen-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 32px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}
</style> 